<template>
  <form class="auth__form" @submit.prevent="">
    <header>
      <h3>Регистрация</h3>
    </header>
    <main>
      <Input
        v-model="$v.form.firstName.$model"
        :error="$v.form.firstName.$error"
        :error-message="formErrors.firstName"
        label="Имя"
        placeholder="Укажи имя"
        type="text"
      />
      <Input
        v-model="$v.form.secondName.$model"
        :error="$v.form.secondName.$error"
        :error-message="formErrors.secondName"
        label="Фамилия"
        placeholder="Укажи фамилию"
        type="text"
      />
      <Input
        v-model="$v.form.email.$model"
        :error="!editing.email && ($v.form.email.$error || !!formErrors.email)"
        :error-message="formErrors.email"
        label="Email"
        placeholder="Укажи e-mail"
        type="email"
        @focus="editing.email = true"
        @blur="editing.email = false"
      />
      <Input
        v-model="$v.form.phone.$model"
        :error="!editing.phone && $v.form.phone.$error"
        label="Номер телефона"
        type="phone"
        @focus="editing.phone = true"
        @blur="editing.phone = false"
      />
      <Input
        v-model="$v.form.city.$model"
        :error="$v.form.city.$error"
        class="double-col"
        label="Город"
        placeholder="Укажи город"
        type="city"
      />

      <Input
        ref="passwordInput"
        v-model="$v.form.password.$model"
        :error="
          !editing.password &&
          ($v.form.password.$error || !passwordRepeated || !!formErrors.password)
        "
        :error-message="formErrors.password"
        :is-tooltip="true"
        :tooltip-options="{
          placement: 'top',
          trigger: 'hover manual',
          open: editing.password,
          autoHide: false
        }"
        :new-password="true"
        label="Пароль"
        placeholder="Введи пароль"
        type="password"
        @focus="editing.password = true"
        @blur="editing.password = false"
        @mouseenter="editing.password = true"
        @mouseleave="editing.password = false"
      >
        <template #tooltip>
          <PasswordPopup :password-validator="$v.form.password" />
        </template>
      </Input>
      <Input
        v-model="$v.form.passwordRepeat.$model"
        :error="!editing.passwordRepeat && ($v.form.passwordRepeat.$error || !passwordRepeated)"
        :new-password="true"
        label="Повтори пароль"
        placeholder="Введи пароль повторно"
        type="password"
        @focus="editing.passwordRepeat = true"
        @blur="editing.passwordRepeat = false"
      />
      <Checkbox
        :value="form.agreedToTerms"
        class="double-col agreement"
        @input="agreeToTerms($event)"
      >
        Я даю согласие на обработку моих персональных данных и принимаю условия
        <a
          href="https://docs.google.com/document/d/1l9I4EyDn7thrLgVAWwM-Eh4r_B5XS-2r-3m6dbZT0J0/edit"
          target="_blank"
          rel="noopener noreferrer"
        >
          Политики Конфиденциальности
        </a>
        и
        <a
          href="https://docs.google.com/document/d/1Z4I_yLTtUeJxyKyV8Ps6dcoYHQFZX7_A4sQlG8595pM/edit"
          target="_blank"
          rel="noopener noreferrer"
        >
          Оферты
        </a>
      </Checkbox>
      <Button
        class="double-col"
        :disabled="!canRegister"
        :loading="isLoading"
        @click="submit(form)"
      >
        Зарегистрироваться
      </Button>
      <SocialNetworksAuth class="double-col social-networks--tight" />
    </main>
    <footer>
      <p>
        Уже есть аккаунт?
        <router-link to="/login"> Войти </router-link>
      </p>
    </footer>
  </form>
</template>

<script>
import Input from '@/components/UiKit/v2/Input';
import Button from '@/components/UiKit/v2/Button';
import Checkbox from '@/components/UiKit/Checkbox';
import SocialNetworksAuth from './components/SocialNetworksAuth';
import PasswordPopup from './components/PasswordPopup';
import { required, email, maxLength, minLength } from 'vuelidate/lib/validators';
import { phoneValidator } from '@/functions/phoneValidator';
import { mapState } from 'vuex';

export default {
  name: 'LoginForm',
  components: {
    Input,
    Button,
    Checkbox,
    SocialNetworksAuth,
    PasswordPopup
  },
  data: () => ({
    form: {
      firstName: '',
      secondName: '',
      email: '',
      phone: '',
      city: '',
      password: '',
      passwordRepeat: '',
      agreedToTerms: false
    },
    editing: {
      email: false,
      phone: false,
      password: false,
      passwordRepeat: false
    }
  }),
  computed: {
    ...mapState('security', {
      isLoading: state => state.meta.registering,
      authErrors: state => state.data.authErrors
    }),
    passwordRepeated() {
      return this.form.password === this.form.passwordRepeat;
    },
    canRegister() {
      return !this.$v.form.$invalid && this.passwordRepeated && this.form.agreedToTerms;
    },
    formErrors() {
      const errors = {
        firstName: '',
        secondName: '',
        email: '',
        phone: '',
        city: '',
        password: '',
        passwordRepeat: ''
      };

      if (this.$v.form.$anyError || !this.passwordRepeated) {
        if (this.$v.form.firstName.$error) {
          errors.firstName = !this.$v.form.firstName.maxLength ? 'Слишком длинное имя' : '';
        }
        if (this.$v.form.secondName.$error) {
          errors.secondName = !this.$v.form.secondName.maxLength ? 'Слишком длинная фамилия' : '';
        }
        if (this.$v.form.email.$error) {
          errors.email = !this.$v.form.email.email
            ? 'Некорректный e-mail'
            : !this.$v.form.email.maxLength
            ? 'Слишком длинный e-mail'
            : '';
        }
        if (this.form.password && (this.$v.form.password.$error || !this.passwordRepeated)) {
          errors.password = this.$v.form.password.$error
            ? 'Некорректный пароль'
            : !this.passwordRepeated
            ? 'Пароли не совпадают'
            : '';
        }
      }

      this.authErrors.forEach(e => {
        if (e.field === 'email') {
          errors.email = e.message;
        }
        if (e.field === 'password') {
          errors.password = e.message;
        }
      });

      return errors;
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.$store.dispatch('security/clearAuthErrors');
      }
    }
  },
  methods: {
    agreeToTerms(event) {
      this.form.agreedToTerms = event;
      if (event) {
        this.$v.$touch();
      }
    },
    submit(form) {
      this.$store.dispatch('security/register', form);
    }
  },
  validations: {
    form: {
      firstName: {
        required,
        maxLength: maxLength(256)
      },
      secondName: {
        required,
        maxLength: maxLength(256)
      },
      email: {
        required,
        maxLength: maxLength(256),
        email
      },
      city: {
        required
      },
      phone: {
        required,
        phone: phoneValidator
      },
      password: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(64),
        uppercase: v => /[A-Z]/.test(v),
        lowercase: v => /[a-z]/.test(v),
        numbers: v => /[0-9]/.test(v)
      },
      passwordRepeat: {
        required
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/style/utils';

.auth-layout {
  main {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 20px;
    row-gap: 8px;
    margin-bottom: 20px;

    .agreement {
      @extend %text-500-12;
      color: $ui-light-grey-4-5-contrast;
      margin-top: 18px;
      margin-bottom: 18px;
      display: flex;
      align-items: flex-start;

      &::v-deep .ui-kit-checkbox__input:not(.-active-) {
        border: 1px solid $ui-light-grey-low-contrast;
        border-radius: 4px;
      }

      &::v-deep .ui-kit-checkbox__title {
        @extend %text-500-14;
      }
    }

    .double-col {
      grid-column: span 2;
    }
  }
}

@media all and (max-width: 425px) {
  .auth-layout {
    main {
      grid-template-columns: 1fr;

      .double-col {
        grid-column: span 1;
      }
    }
  }
}
</style>
